import React from 'react';
import { Screen } from '../enums/screen.enum';
import { Trans, useTranslation } from 'react-i18next';
import ContentContainer from '../partials/ContentContainer';
import ActionsContainer from '../partials/ActionsContainer';
import { Button, ButtonGroup, ButtonVariant } from '../components/Button';
import ScreenContainer from '../partials/ScreenContainer';
import { useLocalStorage } from '@uidotdev/usehooks';
import { Constat } from '../models/Constat';
import { Form } from '../components/Form';
import { InputField, InputFieldType } from '../components/InputField';
import { Vehicle } from '../models/Vehicle';
import VehicleContainer, {
  VehicleContainerVariant,
} from '../partials/VehicleContainer';
import DriverImage from '../assets/images/content/person.png';
import Illustration from '../components/Illustration';
import { ConstatUtilsService } from '../services/constat-utils.service';
import Toggle from '../components/Toggle';

export default function Step1DriverScreen({
  isActive,
  setScreen,
}: {
  isActive: boolean;
  setScreen: (screen: Screen) => void;
}) {
  const { t } = useTranslation();

  const constatUtilsService = new ConstatUtilsService();

  const [constat, setConstat] = useLocalStorage('constat', new Constat());
  const [emailValid, setEmailValid] = React.useState(
    !constat?.vehicleA?.driverEmail ||
      constatUtilsService.validEmail(constat?.vehicleA?.driverEmail),
  );
  const [phoneValid, setPhoneValid] = React.useState(
    !constat?.vehicleA?.driverTel ||
      constatUtilsService.validCellphone(constat?.vehicleA?.driverTel),
  );
  const [driverIsInsured, setDriverIsInsured] = React.useState(
    !constat?.vehicleA?.fleetVehicle,
  );

  React.useEffect(() => {
    if (isActive && driverIsInsured) {
      setConstat((prevConstat) => {
        const newConstat = new Constat(prevConstat);
        if (!newConstat.vehicleA) {
          newConstat.vehicleA = new Vehicle();
        }
        newConstat.vehicleA.insuredName = newConstat.vehicleA.driverName;
        newConstat.vehicleA.insuredFirstname =
          newConstat.vehicleA.driverFirstname;
        newConstat.vehicleA.insuredTel = newConstat.vehicleA.driverTel;
        newConstat.vehicleA.insuredEmail = newConstat.vehicleA.driverEmail;
        return newConstat;
      });
    }
  }, [driverIsInsured]);

  const handleChange = (
    fieldName: string,
    value: any,
    insuredFieldName?: string,
  ) => {
    setConstat((prevConstat) => {
      const newConstat = new Constat(prevConstat);
      if (!newConstat.vehicleA) {
        newConstat.vehicleA = new Vehicle();
      }
      newConstat.vehicleA.setField(fieldName, value);
      if (driverIsInsured && insuredFieldName) {
        newConstat.vehicleA.setField(insuredFieldName, value);
      }
      return newConstat;
    });
  };

  return !isActive ? (
    <></>
  ) : (
    <ScreenContainer
      title={
        <Trans i18nKey={'screens.step1-driver.title'}>
          <strong></strong>
        </Trans>
      }
      timelineStep={1}
      extraCssClasses={['screen-step', 'step1-driver']}
    >
      <ContentContainer>
        <h3>{t('screens.step1-driver.instructions')}</h3>
        <VehicleContainer variant={VehicleContainerVariant.A}>
          <h2>
            <Illustration.Image src={DriverImage} alt=""></Illustration.Image>
            {t('screens.step1-driver.second-title')}
          </h2>
        </VehicleContainer>
        <Form
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
          }}
        >
          <InputField
            name="driverName"
            id="driverName"
            type={InputFieldType.Text}
            required={true}
            label={t('vehicle.label.driverName')}
            value={constat.vehicleA?.driverName || ''}
            onChange={(value) =>
              handleChange('driverName', value, 'insuredName')
            }
          />
          <InputField
            name="driverFirstname"
            id="driverFirstname"
            type={InputFieldType.Text}
            required={true}
            label={t('vehicle.label.driverFirstname')}
            value={constat.vehicleA?.driverFirstname || ''}
            onChange={(value) =>
              handleChange('driverFirstname', value, 'insuredFirstname')
            }
          />
          <InputField
            name="driverTel"
            id="driverTel"
            type={InputFieldType.Phone}
            required={true}
            label={t('vehicle.label.driverTel')}
            value={constat.vehicleA?.driverTel || ''}
            setValid={setPhoneValid}
            validateWith={constatUtilsService.validCellphone}
            onChange={(value) => handleChange('driverTel', value, 'insuredTel')}
          />
          <InputField
            name="driverAddress"
            id="driverAddress"
            type={InputFieldType.Address}
            required={true}
            label={t('vehicle.label.driverAddress')}
            value={constat.vehicleA?.driverAddress || ''}
            onChange={(value) => handleChange('driverAddress', value)}
          />
          <InputField
            name="driverEmail"
            id="driverEmail"
            type={InputFieldType.Email}
            required={true}
            label={t('vehicle.label.driverEmail')}
            value={constat.vehicleA?.driverEmail || ''}
            setValid={setEmailValid}
            validateWith={constatUtilsService.validEmail}
            onChange={(value) =>
              handleChange('driverEmail', value, 'insuredEmail')
            }
          />
          {constatUtilsService.drivingLicenceNumberRequired(
            constat.vehicleA,
          ) && (
            <InputField
              name="driverDrivingLicenceNumber"
              id="driverDrivingLicenceNumber"
              type={InputFieldType.Text}
              required={true}
              label={t('vehicle.label.driverDrivingLicenceNumber')}
              value={constat.vehicleA?.driverDrivingLicenceNumber || ''}
              onChange={(value) =>
                handleChange('driverDrivingLicenceNumber', value)
              }
            />
          )}
        </Form>

        <p className={'question'}>
          {t('screens.step1-driver.ask-driver-insured')}
        </p>
        <Toggle
          id={'driverIsInsured'}
          name={'driverIsInsured'}
          leftLabel={t('common.no')}
          rightLabel={t('common.yes')}
          isChecked={driverIsInsured}
          onChange={setDriverIsInsured}
        ></Toggle>
      </ContentContainer>

      <ActionsContainer>
        <ButtonGroup>
          <Button
            variant={ButtonVariant.Green}
            isOutlined
            onClick={() =>
              setScreen(
                !constat.vehicleA?.fleetVehicle
                  ? Screen.step1ChoiceType
                  : Screen.step1Summary,
              )
            }
          >
            {t('common.previous')}
          </Button>
          <Button
            variant={ButtonVariant.Green}
            isDisabled={
              !emailValid ||
              !phoneValid ||
              !constatUtilsService.isVehicleDriverComplete(
                new Vehicle(constat.vehicleA),
              )
            }
            onClick={() =>
              setScreen(
                driverIsInsured ? Screen.step1Vehicle : Screen.step1Insured,
              )
            }
          >
            {t('common.next')}
          </Button>
        </ButtonGroup>
      </ActionsContainer>
    </ScreenContainer>
  );
}
